// Section access editor — small focused popover/modal. Opened from the
// kebab menu's "Edit access" action. Lets owners/admins toggle which
// membership roles can see this section. Owner is always implicit so we
// don't show it as a toggle (you can never hide a section from owners).

import { useEffect, useRef, useState } from "react"
import { useStore } from "../../stores/store"
import {
  sectionsStore, closeSectionEditor, updateSection,
} from "../../stores/sectionsStore"
import { useBootstrap, useTranslations } from "../../lib/translations"

export function SectionEditor({ sectionId }) {
  const t = useTranslations()
  const bootstrap = useBootstrap()
  const sections = useStore(sectionsStore, (s) => s.sections)
  const saving = useStore(sectionsStore, (s) => s.saving)
  const error  = useStore(sectionsStore, (s) => s.editError)
  const section = sections.find((s) => s.id === sectionId)
  const modalRef = useRef(null)

  // Owner is always included implicitly — they always see everything,
  // so the checkbox only confuses. Hide it from the toggle UI.
  const allRoles = (bootstrap.available_roles || []).filter((r) => r !== "owner")

  const [roles, setRoles] = useState(() => (section?.visible_to_roles || []).filter((r) => r !== "owner"))

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeSectionEditor() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  if (!section) return null

  const onScrim = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) closeSectionEditor()
  }

  const toggle = (r) => {
    setRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r])
  }

  const save = async (e) => {
    e?.preventDefault?.()
    try {
      // Persist roles + force-include "owner" so the rule stays
      // server-side. Owners would see the section anyway via the
      // owner-bypass in Ability, but the explicit entry keeps the
      // visible_to_roles list semantically correct.
      await updateSection(bootstrap.paths, section.id, {
        visible_to_roles: [...roles, "owner"],
      })
    } catch (err) {
      // already surfaced via store.editError
    }
  }

  return (
    <div className="cm-scrim" onMouseDown={onScrim}>
      <div className="cm-section-editor" ref={modalRef}>
        <div className="cm-section-editor-head">
          <h3>{t("section_editor.heading", { name: section.name })}</h3>
          <button type="button" className="cm-x" onClick={closeSectionEditor}>✕</button>
        </div>
        <p className="cm-section-editor-hint">{t("section_editor.hint")}</p>

        <form onSubmit={save}>
          <div className="cm-section-editor-roles">
            {allRoles.map((r) => (
              <label key={r} className={`cm-role-check ${roles.includes(r) ? "on" : ""}`}>
                <input type="checkbox" checked={roles.includes(r)} onChange={() => toggle(r)} />
                <span>{r}</span>
              </label>
            ))}
          </div>

          <div className="cm-section-editor-foot">
            <span className="cm-section-editor-note">{t("section_editor.owner_note")}</span>
            <div className="cm-section-editor-actions">
              <button type="button" onClick={closeSectionEditor} disabled={saving}>
                {t("section_editor.cancel")}
              </button>
              <button type="submit" disabled={saving} className="primary">
                {saving ? t("section_editor.saving") : t("section_editor.save")}
              </button>
            </div>
          </div>
          {error && <div className="cm-section-editor-error">{error}</div>}
        </form>
      </div>
    </div>
  )
}
